'use client'

import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface EmptyStateProps {
  icon?: ReactNode
  title: string
  description: string
  action?: {
    label: string
    href?: string
    onClick?: () => void
  }
  secondaryAction?: {
    label: string
    href?: string
    onClick?: () => void
  }
  className?: string
}

/**
 * Reusable Empty State Component
 * Provides consistent, engaging empty states across the app
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className = '',
}: EmptyStateProps) {
  const defaultIcon = (
    <svg
      className="w-16 h-16 text-stone-300"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  )

  const renderAction = (actionItem: typeof action | typeof secondaryAction, isPrimary = false) => {
    if (!actionItem) return null

    const buttonClass = isPrimary
      ? 'inline-flex items-center justify-center px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 transition-all duration-200'
      : 'inline-flex items-center justify-center px-6 py-3 rounded-xl border border-stone-200 text-stone-800 font-semibold hover:border-emerald-300 hover:text-emerald-700 hover:shadow-md transition-all duration-200'

    if (actionItem.href) {
      return (
        <Link to={actionItem.href} className={buttonClass}>
          {actionItem.label}
        </Link>
      )
    }

    if (actionItem.onClick) {
      return (
        <button onClick={actionItem.onClick} className={buttonClass}>
          {actionItem.label}
        </button>
      )
    }

    return null
  }

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center ${className}`}>
      <div className="flex justify-center mb-4">
        {icon || defaultIcon}
      </div>

      <h3 className="text-xl font-display font-bold text-stone-900 mb-2">{title}</h3>
      <p className="text-stone-600 max-w-md mx-auto mb-6">{description}</p>

      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {action && renderAction(action, true)}
          {secondaryAction && renderAction(secondaryAction, false)}
        </div>
      )}
    </div>
  )
}

